import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import { hasMarketingParams, MarketingParams } from '@/utils/marketingParams'

/**
 * Dedicated, app-wide marketing-attribution store.
 *
 * Intentionally separate from the cart store so it is decoupled from the
 * booking flow and is never wiped by `clearCart()`. Persisted to localStorage
 * (default storage) so attribution survives reloads and the full-page
 * `/tac-book` navigation.
 *
 * Greenfield lead-attribution code is the intended consumer: read
 * `useAttributionStore((s) => s.firstTouch)` reactively, or
 * `useAttributionStore.getState().lastTouch` imperatively at lead/order submit.
 */

export interface AttributionMeta {
  path?: string
  at?: string
}

export interface AttributionStore {
  /** The first marketing params we ever captured for this visitor (set once). */
  firstTouch: MarketingParams | null
  /** The most recent marketing params captured. */
  lastTouch: MarketingParams | null
  /** Path the first-touch attribution was captured on. */
  landingPath?: string
  /** ISO timestamp of the first-touch capture. */
  capturedAt?: string
  hasHydrated?: boolean
  captureAttribution: (params: MarketingParams, meta?: AttributionMeta) => void
  clearAttribution: () => void
  setHasHydrated: (state: boolean) => void
}

export const ATTRIBUTION_STORE_KEY = 'attribution-store'

const useAttributionStore = create<AttributionStore>()(
  persist(
    (set) => ({
      firstTouch: null,
      lastTouch: null,
      landingPath: undefined,
      capturedAt: undefined,
      hasHydrated: false,
      captureAttribution: (params, meta) =>
        set((state) => {
          // Ignore empty captures so internal navigations never clobber attribution.
          if (!hasMarketingParams(params)) return state

          const isFirstTouch = state.firstTouch === null

          return {
            lastTouch: params,
            ...(isFirstTouch
              ? {
                  firstTouch: params,
                  landingPath: meta?.path,
                  capturedAt: meta?.at,
                }
              : {}),
          }
        }),
      clearAttribution: () =>
        set(() => ({
          firstTouch: null,
          lastTouch: null,
          landingPath: undefined,
          capturedAt: undefined,
        })),
      setHasHydrated: (state) => set({ hasHydrated: state }),
    }),
    {
      name: ATTRIBUTION_STORE_KEY,
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true)
      },
    },
  ),
)

export default useAttributionStore
